import {
  ANDROID_APK_FILE_NAME,
  ANDROID_APK_STORAGE_PATH,
  androidVersionManifestHostingUrl,
  readAndroidApkConfig,
} from '../../core/config/androidApkConfig';
import {
  evaluateAndroidUpdateOffer,
  parseAndroidVersionManifest,
  type AndroidUpdateOffer,
  type AndroidVersionManifest,
} from '../../core/config/androidUpdate';
import {
  ensureApkInstallPermission,
  installApkFromPath,
  isAndroidNativeRuntime,
} from '../../adapters/mobile/apkInstaller';
import { readInstalledAndroidVersion } from './androidInstalledVersion';
import { resolveAndroidApkDownload } from './androidApkDownload';

const DISMISS_KEY = 'alea.androidUpdate.dismissedVersion';
const CACHE_APK_RELATIVE_PATH = `updates/${ANDROID_APK_FILE_NAME}`;

async function storageDownloadUrl(path: string): Promise<string | null> {
  try {
    const [{ storage }, { ref, getDownloadURL }] = await Promise.all([
      import('../../config/firebase'),
      import('firebase/storage'),
    ]);
    return await getDownloadURL(ref(storage, path));
  } catch {
    return null;
  }
}

async function fetchManifestFromUrl(url: string): Promise<AndroidVersionManifest | null> {
  const res = await fetch(url, { cache: 'no-store', credentials: 'omit' });
  if (!res.ok) return null;
  return parseAndroidVersionManifest(await res.json());
}

/** Best-effort remove of a previously downloaded update APK from app cache. */
export async function clearCachedAndroidUpdateApk(): Promise<void> {
  if (!isAndroidNativeRuntime()) return;
  try {
    const { Filesystem, Directory } = await import('@capacitor/filesystem');
    await Filesystem.deleteFile({
      path: CACHE_APK_RELATIVE_PATH,
      directory: Directory.Cache,
    });
  } catch {
    // Missing file or unsupported platform — ignore.
  }
}

export async function fetchAndroidVersionManifest(
  env: Record<string, string | undefined> = import.meta.env as Record<string, string | undefined>,
): Promise<AndroidVersionManifest | null> {
  const config = readAndroidApkConfig(env);
  // Hosting first — works when Google Storage is blocked (Cuba / no VPN).
  const hostingUrl = androidVersionManifestHostingUrl(config);
  if (hostingUrl) {
    try {
      const fromHosting = await fetchManifestFromUrl(hostingUrl);
      if (fromHosting) return fromHosting;
    } catch {
      /* try Storage */
    }
  }
  try {
    const url = await storageDownloadUrl(config.versionManifestPath);
    if (!url) return null;
    return await fetchManifestFromUrl(url);
  } catch {
    // Storage CORS / network blips must not surface as offline crashes from About.
    return null;
  }
}

export async function checkAndroidAppUpdate(options: {
  minAppVersion?: string;
} = {}): Promise<AndroidUpdateOffer | null> {
  if (!isAndroidNativeRuntime()) return null;
  await clearCachedAndroidUpdateApk();
  const [installed, remote] = await Promise.all([
    readInstalledAndroidVersion(),
    fetchAndroidVersionManifest(),
  ]);
  if (!remote) return null;
  return evaluateAndroidUpdateOffer(installed, remote, options);
}

export function readDismissedAndroidUpdateVersion(): string | null {
  try {
    return localStorage.getItem(DISMISS_KEY);
  } catch {
    return null;
  }
}

export function dismissAndroidUpdateVersion(versionName: string): void {
  try {
    localStorage.setItem(DISMISS_KEY, versionName);
  } catch {
    /* ignore quota / private mode */
  }
}

export type AndroidUpdateProgress = {
  phase: 'preparing' | 'downloading' | 'installing' | 'done' | 'error';
  messageKey?: string;
};

/**
 * Download the latest APK into cache and hand it to the system installer.
 * The OS install UI takes over; when the user confirms, the current process is replaced.
 */
export async function downloadAndInstallAndroidUpdate(
  offer: AndroidUpdateOffer,
  onProgress?: (progress: AndroidUpdateProgress) => void,
): Promise<{ ok: boolean; reason?: string }> {
  if (!isAndroidNativeRuntime()) {
    // Web / desktop: fall back to browser download of the APK.
    const release = await resolveAndroidApkDownload();
    if (!release) return { ok: false, reason: 'unavailable' };
    const anchor = document.createElement('a');
    anchor.href = release.url;
    anchor.download = release.fileName;
    anchor.rel = 'noopener noreferrer';
    anchor.target = '_blank';
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    return { ok: true };
  }

  onProgress?.({ phase: 'preparing' });
  const allowed = await ensureApkInstallPermission();
  if (!allowed) {
    return { ok: false, reason: 'install_permission_required' };
  }

  // Prefer Hosting/CDN URL (works when Google Storage is blocked), then Storage.
  const hostingRelease = await resolveAndroidApkDownload();
  const apkPath = offer.remote.apkStoragePath || ANDROID_APK_STORAGE_PATH;
  const url = hostingRelease?.url || (await storageDownloadUrl(apkPath));
  if (!url) return { ok: false, reason: 'unavailable' };

  onProgress?.({ phase: 'downloading' });
  try {
    const { Filesystem, Directory } = await import('@capacitor/filesystem');
    const downloaded = await Filesystem.downloadFile({
      path: CACHE_APK_RELATIVE_PATH,
      url,
      directory: Directory.Cache,
    });
    const absolutePath = downloaded.path;
    onProgress?.({ phase: 'installing' });
    if (!absolutePath) {
      const uri = await Filesystem.getUri({
        path: CACHE_APK_RELATIVE_PATH,
        directory: Directory.Cache,
      });
      await installApkFromPath(uri.uri);
    } else {
      await installApkFromPath(absolutePath);
    }
    // Keep the cached APK until the next update check — the system installer
    // may still read the FileProvider URI after this method returns.
    onProgress?.({ phase: 'done' });
    return { ok: true };
  } catch (error) {
    const reason = error instanceof Error ? error.message : 'install_failed';
    onProgress?.({ phase: 'error', messageKey: reason });
    await clearCachedAndroidUpdateApk();
    if (String(reason).includes('install_permission_required')) {
      return { ok: false, reason: 'install_permission_required' };
    }
    return { ok: false, reason: 'install_failed' };
  }
}
